// src/stores/CompanyStore.ts
import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from './index';
import companyService, {
    CompanyProfile,
    CreateCompanyParams,
    UpdateCompanyParams
} from '@/services/companyService';

export default class CompanyStore {
    rootStore: RootStore;

    // ✅ Core state
    currentCompanyProfile: CompanyProfile | null = null;
    industries: any[] = [];

    // ✅ UI State
    isLoading: boolean = false;
    error: string | null = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, { rootStore: false });
    }

    // ===== GETTERS =====

    get isCompanyProfileComplete() {
        if (!this.currentCompanyProfile) return false;

        const profile = this.currentCompanyProfile;
        const requiredFields = [
            'companyName',
            'businessRegistrationNumber',
            'taxId',
            'companyType'
        ];

        return requiredFields.every(field => profile[field as keyof CompanyProfile]);
    }

    get companyVerificationStatus() {
        if (!this.currentCompanyProfile) return 'unverified';
        return this.currentCompanyProfile.verificationLevel;
    }

    // ===== SETTERS =====

    setLoading(loading: boolean) {
        this.isLoading = loading;
    }

    setError(error: string | null) {
        this.error = error;
    }

    // ===== CORE COMPANY PROFILE METHODS =====

    /**
     * Tạo company profile mới
     */
    async createCompanyProfile(profileData: CreateCompanyParams) {
        this.setLoading(true);
        this.setError(null);

        try {
            const companyProfile = await companyService.createCompanyProfile(profileData);

            runInAction(() => {
                this.currentCompanyProfile = companyProfile;
                this.isLoading = false;
            });

            return companyProfile;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Tạo hồ sơ công ty thất bại';
                this.isLoading = false;
            });
            throw err;
        }
    }

    /**
     * Lấy company profile hiện tại
     */
    async fetchCurrentCompanyProfile() {
        this.setLoading(true);
        this.setError(null);

        try {
            const companyProfile = await companyService.getCurrentCompanyProfile();

            runInAction(() => {
                this.currentCompanyProfile = companyProfile;
                this.isLoading = false;
            });

            return companyProfile;
        } catch (err: any) {
            runInAction(() => {
                // If no company profile exists, that's not an error
                if (err.response?.status === 404) {
                    this.currentCompanyProfile = null;
                } else {
                    this.error = err.response?.data?.error?.message || 'Lấy thông tin công ty thất bại';
                }
                this.isLoading = false;
            });
            throw err;
        }
    }

    /**
     * Cập nhật company profile hiện tại
     */
    async updateCurrentCompanyProfile(profileData: UpdateCompanyParams) {
        this.setLoading(true);
        this.setError(null);

        try {
            const updatedProfile = await companyService.updateCurrentCompanyProfile(profileData);

            runInAction(() => {
                this.currentCompanyProfile = updatedProfile;
                this.isLoading = false;
            });

            return updatedProfile;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Cập nhật thông tin công ty thất bại';
                this.isLoading = false;
            });
            throw err;
        }
    }

    // ===== FILE UPLOAD METHODS =====

    /**
     * Upload company logo
     */
    async updateCompanyLogo(file: File) {
        this.setLoading(true);
        this.setError(null);

        try {
            const result = await companyService.updateCompanyLogo(file);

            runInAction(() => {
                if (this.currentCompanyProfile) {
                    this.currentCompanyProfile.logo = result.logo;
                }
                this.isLoading = false;
            });

            return result;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Upload logo thất bại';
                this.isLoading = false;
            });
            throw err;
        }
    }

    /**
     * Upload business license
     */
    async uploadBusinessLicense(file: File) {
        this.setLoading(true);
        this.setError(null);

        try {
            const result = await companyService.uploadBusinessLicense(file);

            runInAction(() => {
                if (this.currentCompanyProfile) {
                    this.currentCompanyProfile.businessLicense = result.businessLicense;
                }
                this.isLoading = false;
            });

            return result;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Upload giấy phép kinh doanh thất bại';
                this.isLoading = false;
            });
            throw err;
        }
    }

    /**
     * Upload tax certificate
     */
    async uploadTaxCertificate(file: File) {
        this.setLoading(true);
        this.setError(null);

        try {
            const result = await companyService.uploadTaxCertificate(file);

            runInAction(() => {
                if (this.currentCompanyProfile) {
                    this.currentCompanyProfile.taxCertificate = result.taxCertificate;
                }
                this.isLoading = false;
            });

            return result;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Upload chứng nhận thuế thất bại';
                this.isLoading = false;
            });
            throw err;
        }
    }

    // ===== UTILITIES =====

    /**
     * Lấy danh sách industries
     */
    async fetchIndustries() {
        if (this.industries.length > 0) return this.industries; // Cache

        this.setLoading(true);
        this.setError(null);

        try {
            const industries = await companyService.getIndustries();

            runInAction(() => {
                this.industries = industries;
                this.isLoading = false;
            });

            return industries;
        } catch (err: any) {
            runInAction(() => {
                this.error = err.response?.data?.error?.message || 'Lấy danh sách ngành nghề thất bại';
                this.isLoading = false;
            });
            throw err;
        }
    }

    /**
     * Validate business registration number
     */
    async validateBusinessRegistration(registrationNumber: string) {
        try {
            return await companyService.validateBusinessRegistration(registrationNumber);
        } catch (err: any) {
            this.setError('Xác thực số đăng ký kinh doanh thất bại');
            throw err;
        }
    }

    /**
     * Validate tax ID
     */
    async validateTaxId(taxId: string) {
        try {
            return await companyService.validateTaxId(taxId);
        } catch (err: any) {
            this.setError('Xác thực mã số thuế thất bại');
            throw err;
        }
    }

    // ===== HELPER METHODS =====

    /**
     * Clear errors
     */
    clearError() {
        this.error = null;
    }

    /**
     * Clear current company profile
     */
    clearCurrentCompanyProfile() {
        this.currentCompanyProfile = null;
    }

    /**
     * Reset store to initial state
     */
    reset() {
        this.currentCompanyProfile = null;
        this.industries = [];
        this.isLoading = false;
        this.error = null;
    }

    /**
     * Check if company has specific verification document
     */
    hasDocument(documentType: 'businessLicense' | 'taxCertificate' | 'verificationDocuments'): boolean {
        if (!this.currentCompanyProfile) return false;

        const document = this.currentCompanyProfile[documentType];

        if (documentType === 'verificationDocuments') {
            return Array.isArray(document) && document.length > 0;
        }

        return !!document;
    }

    /**
     * Get profile completion percentage
     */
    get profileCompletionPercentage(): number {
        if (!this.currentCompanyProfile) return 0;

        const profile = this.currentCompanyProfile;
        const totalFields = 10; // Số fields quan trọng
        let completedFields = 0;

        // Required fields
        if (profile.companyName) completedFields++;
        if (profile.businessRegistrationNumber) completedFields++;
        if (profile.taxId) completedFields++;
        if (profile.companyType) completedFields++;

        // Optional but important fields
        if (profile.businessDescription) completedFields++;
        if (profile.headquarters) completedFields++;
        if (profile.contactInfo) completedFields++;
        if (profile.logo) completedFields++;
        if (profile.businessLicense) completedFields++;
        if (profile.industry) completedFields++;

        return Math.round((completedFields / totalFields) * 100);
    }

    /**
     * Get next steps for profile completion
     */
    get nextCompletionSteps(): string[] {
        if (!this.currentCompanyProfile) return ['Tạo hồ sơ công ty'];

        const profile = this.currentCompanyProfile;
        const steps: string[] = [];

        if (!profile.companyName) steps.push('Nhập tên công ty');
        if (!profile.businessRegistrationNumber) steps.push('Nhập số đăng ký kinh doanh');
        if (!profile.taxId) steps.push('Nhập mã số thuế');
        if (!profile.companyType) steps.push('Chọn loại hình doanh nghiệp');
        if (!profile.businessDescription) steps.push('Thêm mô tả doanh nghiệp');
        if (!profile.headquarters) steps.push('Thêm địa chỉ trụ sở');
        if (!profile.contactInfo) steps.push('Thêm thông tin liên hệ');
        if (!profile.logo) steps.push('Upload logo công ty');
        if (!profile.businessLicense) steps.push('Upload giấy phép kinh doanh');
        if (!profile.industry) steps.push('Chọn ngành nghề');

        return steps.slice(0, 3); // Chỉ show 3 bước tiếp theo
    }
}